import * as React from 'react'

import type { IGetResourceTemplatesQuery } from '~/client/graph/operations/generated/Resources.generated'

export type ResourceTemplate = NonNullable<
  IGetResourceTemplatesQuery['resourceTemplates']
>[number]

export const templateIdOf = (t: { id?: string | null }): string | null =>
  t.id == null ? null : String(t.id)

interface UseResolvedTemplateParams {
  enabled: boolean
  chosenTemplateId: string | null
  templateAllowList?: string[] | null
  templates: ResourceTemplate[]
  loading: boolean
  /**
   * Resolve the project's default template instead of asking, when more than one
   * exists. For surfaces where picking a form is not the point of the interaction -
   * drawing on the map, where the object is already half-created - the picker reads
   * as a step in the way rather than a choice being offered.
   */
  preferDefaultTemplate?: boolean
}

interface UseResolvedTemplateResult {
  loading: boolean
  templates: ResourceTemplate[]
  effectiveTemplateId: string | null
  effectiveTemplate: ResourceTemplate | null
  needsSelection: boolean
  isEmpty: boolean
}

const EMPTY_TEMPLATES: ResourceTemplate[] = []

export const useResolvedTemplate = ({
  enabled,
  chosenTemplateId,
  templateAllowList,
  templates: allTemplates,
  loading,
  preferDefaultTemplate = false,
}: UseResolvedTemplateParams): UseResolvedTemplateResult => {
  const templates = React.useMemo(() => {
    if (templateAllowList == null) return allTemplates
    if (!templateAllowList.length) return EMPTY_TEMPLATES
    const allowedSet = new Set(templateAllowList)
    return allTemplates.filter(t => {
      const id = templateIdOf(t)
      return id != null && allowedSet.has(id)
    })
  }, [allTemplates, templateAllowList])

  // The template carrying the code prefix is the project's default: it is the one
  // ResourceCodeService falls back to when a record names none, so preferring it here
  // keeps the form a record is created with and the prefix its code gets in agreement.
  const defaultTemplateId = React.useMemo(() => {
    const withPrefix = templates.find(template => !!template.codePrefix)
    return withPrefix ? templateIdOf(withPrefix) : null
  }, [templates])

  const autoTemplateId =
    templates.length === 1
      ? templateIdOf(templates[0])
      : preferDefaultTemplate
        ? defaultTemplateId
        : null
  const effectiveTemplateId = chosenTemplateId ?? autoTemplateId

  const effectiveTemplate = React.useMemo(() => {
    if (!effectiveTemplateId) return null
    return templates.find(t => templateIdOf(t) === effectiveTemplateId) ?? null
  }, [templates, effectiveTemplateId])

  return {
    loading,
    templates,
    effectiveTemplateId,
    effectiveTemplate,
    needsSelection: templates.length > 1 && !effectiveTemplateId,
    isEmpty: enabled && !loading && templates.length === 0,
  }
}
